import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, MapPin, Check, X, MessageCircle, FileText, Info, BedDouble, Sparkles, ShieldCheck, CircleOff, Flame, Ticket } from "lucide-react";
import { fetchPackage } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { waPackage } from "@/config/siteConfig";
import { trackEvent } from "@/lib/analytics";
import { useQuote } from "@/context/QuoteContext";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Skeleton } from "@/components/ui/skeleton";
import EnquiryForm from "@/components/EnquiryForm";
import { PriceTag, TagBadge } from "@/components/PackageCard";
import { FAQSection, FinalCTA } from "@/components/home/SocialSections";
import NotFoundPage from "@/pages/NotFoundPage";

const OBJECTION_ICONS = [ShieldCheck, CircleOff, Flame, Ticket];

const PackageDetailPage = () => {
  const { slug } = useParams();
  const { openQuote } = useQuote();
  const { data: pkg, isLoading, isError } = useQuery({
    queryKey: ["package", slug],
    queryFn: () => fetchPackage(slug),
    retry: false,
  });

  useSEO(pkg ? {
    title: `${pkg.name} ${pkg.duration} | Kashmir Package | Book Kashmir`,
    description: pkg.description.slice(0, 155),
    path: `/packages/${slug}`,
  } : {});

  useEffect(() => {
    if (pkg) trackEvent("package_view", { package: pkg.name });
  }, [pkg]);

  if (isLoading) {
    return (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="package-detail-loading">
        <Skeleton className="h-72 rounded-2xl" />
        <Skeleton className="mt-6 h-8 w-1/2" />
        <Skeleton className="mt-3 h-4 w-2/3" />
      </main>
    );
  }
  if (isError || !pkg) return <NotFoundPage />;

  return (
    <main data-testid="package-detail-page">
      <section className="relative" data-testid="package-hero">
        <div className={`absolute inset-0 ${pkg.image_fit === "contain" ? "bg-[#0F2F24]" : ""}`}>
          <img src={pkg.image} alt={`${pkg.name} - Kashmir`}
            className={`h-full w-full ${pkg.image_fit === "contain" ? "object-contain" : "object-cover"}`} />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          {pkg.tag && <TagBadge tag={pkg.tag} slug={pkg.slug} className="mb-4" />}
          <p className="text-xs font-mono uppercase tracking-[0.2em] font-semibold text-[#D99B26] mb-4 flex items-center gap-2">
            <Clock className="h-3.5 w-3.5" /> {pkg.duration}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold tracking-tight text-white text-balance" data-testid="package-title">{pkg.name}</h1>
          <p className="mt-3 flex items-center gap-2 text-sm font-medium text-white/80">
            <MapPin className="h-4 w-4 text-[#D99B26]" /> {pkg.route.join(" → ")}
          </p>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-white/85 leading-relaxed">{pkg.description}</p>
          {pkg.badges && (
            <div className="mt-5 flex max-w-3xl flex-wrap gap-2" data-testid="package-trust-badges">
              {pkg.badges.map((b) => (
                <span key={b} className="flex items-center gap-1.5 rounded-full bg-[#D99B26] px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0F2F24] shadow-md">
                  <Check className="h-3 w-3" strokeWidth={3} /> {b}
                </span>
              ))}
            </div>
          )}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button onClick={() => openQuote(pkg.name)} data-testid="package-get-package-button"
              className="bg-[#D99B26] text-[#0F2F24] hover:bg-[#c98f22] font-semibold rounded-full px-6 h-11">
              <FileText className="mr-2 h-4 w-4" /> Get This Package
            </Button>
            <Button asChild variant="outline" className="rounded-full px-6 h-11 border-white/60 bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm">
              <a href={waPackage(pkg.name)} target="_blank" rel="noopener noreferrer" data-testid="package-whatsapp-button"
                onClick={() => trackEvent("whatsapp_click", { location: "package_page", package: pkg.name })}>
                <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-14">
          <section data-testid="package-overview">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0F2F24] mb-4">Trip Overview</h2>
            <p className="text-base text-slate-600 leading-relaxed">{pkg.focus}</p>
            {pkg.pricing_tiers ? (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="package-pricing-tiers">
                {pkg.pricing_tiers.map((t) => (
                  <div key={t.name} className="rounded-xl bg-white p-5 ring-1 ring-[#0F2F24]/10 shadow-sm">
                    <p className="text-sm font-bold text-[#0F2F24]">{t.name}</p>
                    <p className="mt-2">
                      <span className="text-xs text-slate-500">Starting from </span>
                      <span className="text-2xl font-bold text-[#0F2F24]">₹{t.price.toLocaleString("en-IN")}</span>
                      <span className="text-xs text-slate-500"> /person</span>
                    </p>
                    <p className="mt-2 text-xs sm:text-sm text-slate-600 leading-relaxed">{t.note}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-lg bg-[#E3EAE4]/70 p-4">
                <PriceTag price={pkg.price} />
              </div>
            )}
          </section>

          <section data-testid="package-highlights">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0F2F24] mb-5">Package Highlights</h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pkg.highlights.map((h) => (
                <li key={h} className="flex items-start gap-2.5 rounded-lg bg-white p-4 text-sm text-[#1E2922] ring-1 ring-[#0F2F24]/8">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#C28519]" /> {h}
                </li>
              ))}
            </ul>
          </section>

          {pkg.objection_cards && (
            <section data-testid="package-objection-cards">
              <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0F2F24] mb-5">Why First-Timers Choose Us</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {pkg.objection_cards.map((c, i) => {
                  const Icon = OBJECTION_ICONS[i % OBJECTION_ICONS.length];
                  return (
                    <div key={c.title} className="rounded-xl bg-white p-5 ring-1 ring-[#0F2F24]/8 shadow-sm transition-shadow hover:shadow-md" data-testid={`objection-card-${i + 1}`}>
                      <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#0F2F24]">
                        <Icon className="h-5 w-5 text-[#D99B26]" />
                      </span>
                      <h3 className="font-serif text-lg font-semibold text-[#0F2F24]">{c.title}</h3>
                      <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{c.text}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section data-testid="package-itinerary">            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0F2F24] mb-5">Detailed Itinerary</h2>
            <Accordion type="single" collapsible defaultValue="day-1" className="w-full">
              {pkg.itinerary.map((d) => (
                <AccordionItem key={d.day} value={`day-${d.day}`} data-testid={`itinerary-day-${d.day}`}>
                  <AccordionTrigger className="text-left">
                    <span className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0F2F24] text-xs font-bold text-[#D99B26]">{d.day}</span>
                      <span className="text-sm sm:text-base font-semibold text-[#0F2F24]">Day {d.day} — {d.title}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pl-11 text-sm sm:text-base text-slate-600 leading-relaxed">{d.description}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="package-inclusions">
            <div className="rounded-xl bg-white p-6 ring-1 ring-[#0F2F24]/8">
              <h2 className="font-serif text-xl font-semibold text-[#0F2F24] mb-4">Inclusions</h2>
              <ul className="space-y-2.5">
                {pkg.inclusions.map((inc) => (
                  <li key={inc} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" /> {inc}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl bg-white p-6 ring-1 ring-[#0F2F24]/8">
              <h2 className="font-serif text-xl font-semibold text-[#0F2F24] mb-4">Exclusions</h2>
              <ul className="space-y-2.5">
                {pkg.exclusions.map((exc) => (
                  <li key={exc} className="flex items-start gap-2 text-sm text-slate-700">
                    <X className="mt-0.5 h-4 w-4 shrink-0 text-red-500" /> {exc}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section data-testid="package-hotels">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0F2F24] mb-5">Hotel Options</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {pkg.hotels.map((h) => (
                <div key={h.tier} className="rounded-xl bg-white p-5 ring-1 ring-[#0F2F24]/8" data-testid={`hotel-tier-${h.tier.toLowerCase()}`}>
                  <p className="flex items-center gap-2 font-serif text-lg font-semibold text-[#0F2F24]">
                    <BedDouble className="h-4 w-4 text-[#C28519]" /> {h.tier}
                  </p>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{h.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-xl bg-[#E3EAE4]/70 p-6" data-testid="package-important-info">
            <h2 className="flex items-center gap-2 font-serif text-xl font-semibold text-[#0F2F24] mb-4">
              <Info className="h-5 w-5 text-[#C28519]" /> Important Information
            </h2>
            <ul className="space-y-2">
              {pkg.important_info.map((info) => (
                <li key={info} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C28519]" /> {info}
                </li>
              ))}
            </ul>
          </section>

          <section data-testid="package-faqs">
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0F2F24] mb-5">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="w-full rounded-xl bg-white px-5 ring-1 ring-[#0F2F24]/8">
              {pkg.faqs.map((f, i) => (
                <AccordionItem key={i} value={`pkg-faq-${i}`} data-testid={`package-faq-item-${i + 1}`}>
                  <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-[#0F2F24]">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 rounded-2xl bg-white p-6 shadow-lg ring-1 ring-[#0F2F24]/8" data-testid="package-enquiry-sidebar">
            <h2 className="font-serif text-xl font-semibold text-[#0F2F24] mb-1">Enquire About This Package</h2>
            <p className="text-sm text-slate-600 mb-5">Get a personalised quote for {pkg.name}.</p>
            <EnquiryForm packageName={pkg.name} source={`package-${slug}`} compact testIdPrefix="package-enquiry" />
          </div>
        </aside>
      </div>
      <FinalCTA />
    </main>
  );
};

export default PackageDetailPage;
